import { useState, useRef, useEffect } from "react";
import { PanelLeftClose, PanelLeft, EyeOff, Moon, Sun, LogOut } from "lucide-react";
import { useUiStore } from "@/stores/ui-store";
import { useAuthStore, selectUserInfo } from "@/stores/auth-store";
import { useLogtoAdmin } from "@/services/auth/logto-provider";
import { cn } from "@/lib/utils";

declare const __BUILD_HASH__: string;
declare const __BUILD_TIME__: string;

function getInitials(name?: string, email?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "AD";
}

interface SidebarFooterProps {
  collapsed: boolean;
}

export function SidebarFooter({ collapsed }: SidebarFooterProps) {
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore.getState().setTheme;
  const setSidebarState = useUiStore.getState().setSidebarState;
  const sidebarState = useUiStore((s) => s.sidebarState);
  const userInfo = useAuthStore(selectUserInfo);
  const { signOut } = useLogtoAdmin();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const name = userInfo?.name || "Admin";
  const email = userInfo?.email || "";
  const initials = getInitials(userInfo?.name, userInfo?.email);

  const handleThemeToggle = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await signOut(window.location.origin);
  };

  const handleToggle = () => {
    if (sidebarState === "expanded") {
      setSidebarState("collapsed");
    } else {
      setSidebarState("expanded");
    }
  };

  const handleHide = () => {
    setSidebarState("hidden");
  };

  return (
    <div className="mt-auto">
      {/* Build info */}
      {!collapsed && (
        <div className="px-4 py-1.5 text-[10px] leading-tight text-[hsl(var(--sidebar-muted))]/30 select-none">
          <p>build {__BUILD_HASH__.slice(0, 7)}</p>
          <p>
            {new Date(__BUILD_TIME__).toLocaleString("it-IT", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </div>
      )}

      {/* User Menu */}
      <div className="relative border-t border-sidebar-border" ref={userMenuRef}>
        {collapsed ? (
          <div className="flex justify-center p-2">
            <button
              onClick={() => setUserMenuOpen((o) => !o)}
              title={`${name} — ${email}`}
              className="h-10 w-10 rounded-full bg-[hsl(var(--sidebar-active-accent))]/20 flex items-center justify-center text-xs font-semibold text-[hsl(var(--sidebar-active-accent))] hover:bg-[hsl(var(--sidebar-active-accent))]/30 transition-colors"
            >
              {initials}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setUserMenuOpen((o) => !o)}
            className="flex items-center gap-2 w-full rounded-lg p-2 hover:bg-sidebar-accent/50 transition-colors"
          >
            <div className="h-8 w-8 rounded-full bg-[hsl(var(--sidebar-active-accent))]/20 flex items-center justify-center text-xs font-semibold text-[hsl(var(--sidebar-active-accent))] shrink-0">
              {initials}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-medium text-sidebar-foreground truncate">{name}</p>
              {email && (
                <p className="text-xs text-[hsl(var(--sidebar-muted))] truncate">{email}</p>
              )}
            </div>
          </button>
        )}

        {/* Dropdown */}
        {userMenuOpen && (
          <div className={cn(
            "absolute bottom-full mb-2 rounded-lg border border-sidebar-border bg-card shadow-lg z-50",
            collapsed ? "left-0 w-56" : "left-2 right-2",
          )}>
            <div className="px-3 py-2 border-b border-border">
              <p className="text-sm font-medium text-foreground truncate">{name}</p>
              {email && (
                <p className="text-xs text-muted-foreground truncate">{email}</p>
              )}
            </div>
            <div className="p-1">
              <button
                onClick={handleThemeToggle}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                {theme === "dark" ? "Modalita chiara" : "Modalita scura"}
              </button>
              <div className="my-1 border-t border-border" />
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Esci
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Collapse/Hide toggles */}
      <div className="border-t border-sidebar-border p-2 flex items-center gap-1">
        <button
          onClick={handleToggle}
          className={cn(
            "flex items-center justify-center flex-1 rounded-lg p-2 text-[hsl(var(--sidebar-muted))]/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-all duration-200",
            collapsed && "flex-none w-10 h-10",
          )}
          title={collapsed ? "Espandi sidebar" : "Comprimi sidebar"}
        >
          {collapsed ? (
            <PanelLeft className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </button>
        {!collapsed && (
          <button
            onClick={handleHide}
            className="flex items-center justify-center rounded-lg p-2 text-[hsl(var(--sidebar-muted))]/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-all duration-200"
            title="Nascondi sidebar"
          >
            <EyeOff className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
